import React, { useState, useEffect } from 'react';
import { useAuth, api } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Building2, CheckCircle, Clock, Loader2, Search, ArrowUpDown } from 'lucide-react';

const TenantsPage = () => {
  const { token } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name_asc');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [formData, setFormData] = useState({
    cnpj: '',
    company_name: '',
    trading_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    active: true
  });

  useEffect(() => {
    fetchTenants();
  }, [token]);

  const fetchTenants = async () => {
    try {
      const data = await api.get('/tenants', token);
      setTenants(data);
    } catch (error) {
      toast.error('Erro ao carregar organizações');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTenant) {
        await api.put(`/tenants/${editingTenant.id}`, formData, token);
        toast.success('Organização atualizada');
      } else {
        await api.post('/tenants', formData, token);
        toast.success('Organização criada');
      }
      setDialogOpen(false);
      resetForm();
      fetchTenants();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar organização');
    }
  };

  const handleEdit = (tenant) => {
    setEditingTenant(tenant);
    setFormData({
      cnpj: tenant.cnpj,
      company_name: tenant.company_name,
      trading_name: tenant.trading_name || '',
      email: tenant.email,
      phone: tenant.phone || '',
      address: tenant.address || '',
      city: tenant.city || '',
      state: tenant.state || '',
      active: tenant.active
    });
    setDialogOpen(true);
  };

  const handleDelete = async (tenant) => {
    if (!window.confirm(`Remover ${tenant.company_name}?`)) return;
    try {
      await api.delete(`/tenants/${tenant.id}`, token);
      toast.success('Organização removida');
      fetchTenants();
    } catch (error) {
      toast.error('Erro ao remover organização');
    }
  };

  const handleApprove = async (tenant) => {
    try {
      await api.post(`/tenants/${tenant.id}/approve`, {}, token);
      toast.success('Organização aprovada');
      fetchTenants();
    } catch (error) {
      toast.error('Erro ao aprovar organização');
    }
  };

  const resetForm = () => {
    setEditingTenant(null);
    setFormData({
      cnpj: '', company_name: '', trading_name: '', email: '',
      phone: '', address: '', city: '', state: '', active: true
    });
  };

  const filteredTenants = tenants
    .filter(t =>
      t.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.trading_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.cnpj?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      if (sortBy === 'name_asc') return (a.company_name || '').localeCompare(b.company_name || '');
      if (sortBy === 'name_desc') return (b.company_name || '').localeCompare(a.company_name || '');
      if (sortBy === 'created_desc') return new Date(b.created_at) - new Date(a.created_at);
      if (sortBy === 'created_asc') return new Date(a.created_at) - new Date(b.created_at);
      return 0;
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tenants-page">
      <div className="flex items-center justify-between pl-14">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Public Sans' }}>
            Organizações (Tenants)
          </h1>
          <p className="text-slate-500">Gerenciamento de empresas/clientes do sistema</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="new-tenant-btn">
              <Plus className="w-4 h-4 mr-2" /> Nova Organização
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingTenant ? 'Editar Organização' : 'Nova Organização'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>CNPJ *</Label>
                  <Input 
                    placeholder="00.000.000/0001-00"
                    value={formData.cnpj} 
                    onChange={(e) => setFormData({ ...formData, cnpj: e.target.value })} 
                    required 
                    data-testid="tenant-cnpj-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Razão Social *</Label>
                  <Input 
                    value={formData.company_name} 
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })} 
                    required 
                    data-testid="tenant-company-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nome Fantasia</Label>
                  <Input 
                    value={formData.trading_name} 
                    onChange={(e) => setFormData({ ...formData, trading_name: e.target.value })} 
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input 
                    type="email"
                    value={formData.email} 
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })} 
                    required 
                    data-testid="tenant-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Telefone</Label>
                  <Input 
                    value={formData.phone} 
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })} 
                  />
                </div>
                <div className="space-y-2">
                  <Label>Endereço</Label>
                  <Input 
                    value={formData.address} 
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })} 
                  />
                </div>
                <div className="space-y-2">
                  <Label>Cidade</Label>
                  <Input 
                    value={formData.city} 
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })} 
                  />
                </div>
                <div className="space-y-2">
                  <Label>Estado</Label>
                  <Input 
                    value={formData.state} 
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })} 
                    maxLength={2}
                    placeholder="SP"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch 
                  checked={formData.active} 
                  onCheckedChange={(v) => setFormData({ ...formData, active: v })} 
                />
                <Label>Ativo</Label>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                  Cancelar
                </Button>
                <Button type="submit" data-testid="save-tenant-btn">Salvar</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Buscar organizações..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9" data-testid="search-tenants-input" />
            </div>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-52" data-testid="sort-tenants">
                <ArrowUpDown className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name_asc">Nome (A-Z)</SelectItem>
                <SelectItem value="name_desc">Nome (Z-A)</SelectItem>
                <SelectItem value="created_desc">Cadastro (mais recente)</SelectItem>
                <SelectItem value="created_asc">Cadastro (mais antigo)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <ScrollArea className="h-[600px]">
            <table className="data-table">
              <thead>
                <tr>
                  <th>CNPJ</th>
                  <th>Razão Social</th>
                  <th>Nome Fantasia</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Aprovado</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredTenants.map((tenant) => (
                  <tr key={tenant.id} data-testid={`tenant-row-${tenant.id}`}>
                    <td className="font-mono text-sm">{tenant.cnpj}</td>
                    <td className="font-medium">{tenant.company_name}</td>
                    <td className="text-slate-500">{tenant.trading_name || '-'}</td>
                    <td className="text-slate-500">{tenant.email}</td>
                    <td>
                      <Badge className={tenant.active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}>
                        {tenant.active ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </td>
                    <td>
                      {tenant.approved ? (
                        <Badge className="bg-green-100 text-green-700">
                          <CheckCircle className="w-3 h-3 mr-1" /> Aprovado
                        </Badge>
                      ) : (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleApprove(tenant)}
                          className="text-amber-600 border-amber-200 hover:bg-amber-50"
                        >
                          <Clock className="w-3 h-3 mr-1" /> Aprovar
                        </Button>
                      )}
                    </td>
                    <td>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(tenant)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(tenant)} className="text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {tenants.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-slate-500">
                      <Building2 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                      <p>Nenhuma organização cadastrada</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
};

export default TenantsPage;
